import React, { forwardRef } from 'react';
import { Provider } from 'react-redux';
import App from '../App';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import configureStore from '../../store/store';
import { ConfigProvider } from 'antd';
import { Auth0Provider } from '@auth0/auth0-react';
// @ts-ignore
import { currencies as mockCurrencies } from '../../mock_data/mocks';
import IntercomService from '../../utils/IntercomService';
import AppStoreRedirection from '../app_store_element';
import posthog from 'posthog-js';
import { CURRENT_ENV, CURRENT_ENV_ROLE_TYPE, RELEASE_VERSION } from '../../utils/EnvConfigUtils';
import LoginWrapper from '../login_wrapper';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import RouteWithSubRoutes from '../route_with_sub_routes/RouteWithSubRoutes';
import NotFoundPage from '../not_found_page/NotFoundPage';
import { routes } from '../routes/routes';
import SSOProvider from '../SSOProvider';
import ScrollManager from '../scroll_component/ScrollComponent';

const style = require('../../index.less');
const enUS = require('antd/lib/locale-provider/en_US');
const antdStyle = require('antd/dist/antd.less');

function resultsArrayToObject(objArr) {
	return objArr.reduce((acc, el) => {
		acc[el.id] = el;
		return acc;
	}, {});
}

const preloadedState = {
	currencies: {
		fetching: false,
		fetchErrors: [],
		creating: false,
		createErrors: [],
		deleting: false,
		deleteErrors: [],
		records: resultsArrayToObject(mockCurrencies),
	},
};
if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
	Sentry.init({
		dsn: 'https://3caa87e8bbc545a9afa9ad79756cea11@o475993.ingest.sentry.io/6611349',
		integrations: [new BrowserTracing()],
		// We recommend adjusting this value in production, or using tracesSampler
		// for finer control
		tracesSampleRate: 0.5,

		// https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
		// disabling these types of errors because this will need a lot of handling on apis , since this is asynchronous ,
		//  not breaking any user flow but indicate that these calls need to be handled properly with .catch at every promise
		// to be fixed once we solve all higher priority bugs.
		// FIXME: @arnav

		// TLDR; ResizeObserver error is just an event sent by chrome browser and is not needed to be fixed .
		// https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402/4
		ignoreErrors: ['Non-Error promise rejection captured', 'ResizeObserver loop limit exceeded'],
	});
}

posthog.init(
	// new
	'phc_mbelib77jLLIGafTlfFiDTZSKmWbkI7kjxCNybo3NvB',
	{
		api_host: 'https://b.useopenwrench.com',
		ui_host: 'https://us.posthog.com',
		// Label the PostHog Logs stream (see TelemetryLogUtils) at the source so
		// logs are filterable by env/version before any per-log attributes. The
		// service is just "web" (buyer/supplier share the same app); the role is a
		// separate `userType` resource attribute so it can be filtered independently.
		logs: {
			serviceName: 'web',
			environment: CURRENT_ENV,
			serviceVersion: `${RELEASE_VERSION}`,
			resourceAttributes: {
				userType: CURRENT_ENV_ROLE_TYPE,
			},
		},
	}
);

export const store = configureStore(preloadedState);

export default class Root extends React.Component {
	redirectRef = React.createRef<HTMLDivElement>();

	componentDidMount() {
		IntercomService.init();
	}

	render() {
		const customerMode =
			nullSafeGetOrElse('location.pathname', window, '').indexOf('customer-request-portal/') >= 0 ||
			nullSafeGetOrElse('location.pathname', window, '').indexOf('client-request-portal/') >= 0;

		if (customerMode) {
			return (
				<Provider store={store}>
					<BrowserRouter>
						<ConfigProvider locale={enUS}>
							<Switch>
								{routes.map((r, i) => (
									<RouteWithSubRoutes key={i} {...r} />
								))}
								<Route component={NotFoundPage} />
							</Switch>
						</ConfigProvider>
					</BrowserRouter>
				</Provider>
			);
		}
		return (
			<Provider store={store}>
				<BrowserRouter>
					<SSOProvider>
						<ConfigProvider locale={enUS}>
							<AppStoreRedirection ref={this.redirectRef}>
								<LoginWrapper store={store}>
									<App redirectRef={this.redirectRef} />
								</LoginWrapper>
							</AppStoreRedirection>
						</ConfigProvider>
					</SSOProvider>
				</BrowserRouter>
			</Provider>
		);
	}
}
